import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import api from '../../api/api'
import { getImageBlob } from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import { ZoomFrame } from '@/components/ui/zoomable-image'

import { useOfflineMode } from './hooks/useOfflineMode'
import { anonymizeName } from './utils'

const SLOW_CONNECTION_TIMEOUT = 5000

const PackagePhoto = ({
  onPhotoTaken,
  onBack,
  packageNumber,
  totalPackages,
  selectedResident,
  isLoading,
}) => {
  const { t } = useTranslation(['package-room', 'common'])
  const [photo, setPhoto] = useState(null)
  const [photoUuid, setPhotoUuid] = useState(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraError, setCameraError] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const { isConnectionIssue, setHasBadConnection, cachedSettings, isRoneResidencesBuilding } =
    useOfflineMode()

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        setCameraActive(true)
        setCameraError(null)
      }
    } catch (err) {
      console.error('Error accessing camera:', err)
      setCameraError(t('package-room:packagePhoto.cameraError'))
      setCameraActive(false)
    }
  }
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks()
      tracks.forEach((track) => track.stop())
      videoRef.current.srcObject = null
      setCameraActive(false)
    }
  }

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const photoData = canvas.toDataURL('image/jpeg', 0.9)
    setPhoto(photoData)

    stopCamera()

    if (isConnectionIssue) {
      return
    }

    setIsUploading(true)
    setUploadError(null)

    // Detect slow uploads and turn on semi-offline mode
    const uploadTimeout = setTimeout(() => {
      setHasBadConnection(true)
      setIsUploading(false)
    }, SLOW_CONNECTION_TIMEOUT)

    try {
      const blob = await getImageBlob(photoData)
      const timestamp = new Date().getTime()
      const filename = `package_${packageNumber}_${timestamp}.jpg`
      const file = new File([blob], filename, { type: 'image/jpeg' })
      const response = await api.uploadPackageRoomImage(file)

      clearTimeout(uploadTimeout)

      const [imageUuid] = response.data
      setPhotoUuid(imageUuid)
      setIsUploading(false)
    } catch (error) {
      clearTimeout(uploadTimeout)
      console.error('Error uploading image:', error)
      setUploadError(t('package-room:packagePhoto.uploadError'))
      setIsUploading(false)
    }
  }

  const previewSrc = photoUuid
    ? getAssetLink({
        path: 'chitown-images/notification',
        file: photoUuid,
        fallback: { file: 'notification_image.png' },
      })
    : photo

  const handleSubmit = () => {
    onPhotoTaken(photoUuid)
  }

  const handleRetake = () => {
    setPhoto(null)
    setPhotoUuid(null)
    setUploadError(null)
  }

  useEffect(() => {
    if (!photo) {
      startCamera()
    }

    return () => {
      stopCamera()
    }
  }, [photo])

  return (
    <div className="package-room__content package-room__content--full">
      <div className="package-room__navigation">
        <button className="package-room__back" onClick={onBack}>
          <ArrowLeft size={24} />
          {t('common:actions.back')}
        </button>
        <div className="package-room__progress">
          {t('package-room:progress.packageOf', { number: packageNumber, total: totalPackages })}
        </div>
      </div>

      <div className="package-room__header">
        <h2 className="package-room__title">
          {photo
            ? t('package-room:packagePhoto.isLabelVisible')
            : t('package-room:packagePhoto.takePackagePhoto')}
        </h2>
        {selectedResident && (
          <>
            <div className="package-room__resident-info">
              {isRoneResidencesBuilding ? (
                <span className="package-room__resident-name">
                  {t('package-room:packagePhoto.packageFor', { name: selectedResident.apartment })}
                </span>
              ) : (
                <>
                  <span className="package-room__resident-name">
                    {t('package-room:packagePhoto.packageFor', {
                      name: cachedSettings?.maskUsernameEnabled
                        ? anonymizeName(selectedResident.fullName)
                        : selectedResident.fullName,
                    })}
                  </span>
                  <span className="package-room__resident-unit">{selectedResident.apartment}</span>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="package-room__photo-container">
        {!photo ? (
          <React.Fragment>
            {cameraError ? (
              <div className="package-room__camera-error">
                <p>{cameraError}</p>
              </div>
            ) : (
              <div className="package-room__camera-container">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="package-room__camera-preview"
                />
              </div>
            )}
            {/* Hidden canvas used for capturing the photo */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </React.Fragment>
        ) : (
          <div className="package-room__photo-preview">
            {isUploading || (!photoUuid && !isConnectionIssue && !uploadError) ? (
              <div className="package-room__photo-loading">
                <div className="package-room__loading-spinner" />
              </div>
            ) : (
              <ZoomFrame
                className="photo-frame"
                fill
                src={previewSrc}
                title={t('package-room:packagePhoto.imageAlt')}
              >
                <img
                  src={previewSrc}
                  alt={t('package-room:packagePhoto.imageAlt')}
                  className="package-room__photo-image"
                />
              </ZoomFrame>
            )}
            {uploadError && (
              <div className="package-room__upload-error">
                <p>{uploadError}</p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="package-room__action-buttons">
        {photo ? (
          <React.Fragment>
            <button className="package-room__retake-button" onClick={handleRetake}>
              {t('package-room:packagePhoto.noRetake')}
            </button>
            <button
              className="package-room__submit-button"
              onClick={handleSubmit}
              disabled={isUploading || isLoading}
            >
              {isLoading
                ? t('package-room:packagePhoto.submitting')
                : t('package-room:packagePhoto.yesSubmit')}
            </button>
          </React.Fragment>
        ) : (
          <button
            className="package-room__submit-button package-room__submit-button--full-width"
            onClick={capturePhoto}
            disabled={!cameraActive}
          >
            {t('package-room:packagePhoto.capturePhoto')}
          </button>
        )}
      </div>
    </div>
  )
}

PackagePhoto.propTypes = {
  onPhotoTaken: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  packageNumber: PropTypes.number.isRequired,
  totalPackages: PropTypes.number.isRequired,
  selectedResident: PropTypes.object.isRequired,
  selectedCarrier: PropTypes.string,
  isLoading: PropTypes.bool,
}

export default PackagePhoto
